/** Firmware data management with backend integration. */

import type { FirmwareMockItem, FirmwareTab } from './firmware-types'
import { FIRMWARE_TABS, FIRMWARE_FOLDER_MAP, VERSION_KEY_MAP } from './firmware-types'
import { getDeviceColorName } from '@/api/device/protocol'
import { getFirmwareList, convertToUpgradeFormat, groupFirmwareByDevice } from '@/api/firmware'

const FACTORY_ACCOUNT_UUID = '1056752647'

// 缓存后端固件数据
let cachedFirmwareData: Record<FirmwareTab, FirmwareMockItem['historyList']> = {
  earphone: [],
  earphoneBluetooth: [],
  earphoneNoiseCancel: [],
  dongle: [],
}
let lastFetchTime = 0
const CACHE_DURATION = 60000 // 1分钟缓存

/**
 * 从后端获取固件列表
 */
async function fetchBackendFirmware(): Promise<void> {
  const now = Date.now()
  if (now - lastFetchTime < CACHE_DURATION && Object.values(cachedFirmwareData).some(list => list.length > 0)) {
    return // 使用缓存
  }

  try {
    const backendList = await getFirmwareList()
    if (backendList.length > 0) {
      const grouped = groupFirmwareByDevice(backendList)

      // 清空旧数据
      cachedFirmwareData = {
        earphone: [],
        earphoneBluetooth: [],
        earphoneNoiseCancel: [],
        dongle: [],
      }

      // 将后端数据映射到设备类型
      Object.keys(grouped).forEach(deviceName => {
        const tab = deviceNameToTab(deviceName)
        if (tab && grouped[deviceName]) {
          const items = convertToUpgradeFormat(grouped[deviceName])
          cachedFirmwareData[tab] = items
        }
      })

      lastFetchTime = now
    }
  } catch (error) {
    console.warn('Failed to fetch backend firmware:', error)
  }
}

/**
 * 设备名称转换为 Tab ID
 */
function deviceNameToTab(deviceName: string): FirmwareTab | null {
  const mapping: Record<string, FirmwareTab> = {
    'Earphone': 'earphone',
    'EarphoneBluetooth': 'earphoneBluetooth',
    'EarphoneNoiseCancel': 'earphoneNoiseCancel',
    'Dongle': 'dongle',
  }
  return mapping[deviceName] || null
}

export function parseFirmwareVersion(version: string): number[] {
  const raw = String(version || '').trim().replace(/^[vV]/, '')
  if (!raw) return []
  const main = raw.split(/[-_]/)[0]
  const suffix = raw.slice(main.length).replace(/^[-_]/, '')
  const nums = main.split('.').map(n => parseInt(n, 10) || 0)
  if (suffix) nums.push(parseInt(suffix, 10) || 0)
  return nums
}

/**
 * 检查两个版本号的前两段是否匹配
 * 例如: V1.1.8 和 V1.1.10 匹配，V1.1.8 和 V1.2.1 不匹配
 */
export function isMajorMinorMatch(version1: string, version2: string): boolean {
  const v1 = parseFirmwareVersion(version1)
  const v2 = parseFirmwareVersion(version2)

  // 至少需要有两段版本号
  if (v1.length < 2 || v2.length < 2) return false

  // 比较前两段 (major.minor)
  return v1[0] === v2[0] && v1[1] === v2[1]
}

export function compareFirmwareVersion(v1: string, v2: string): number {
  const a = parseFirmwareVersion(v1); const b = parseFirmwareVersion(v2)
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i++) {
    if ((a[i] || 0) > (b[i] || 0)) return 1
    if ((a[i] || 0) < (b[i] || 0)) return -1
  }
  return 0
}

function normalizeColor(value: unknown): 'black' | 'white' | '' {
  const color = getDeviceColorName(value)
  return color === 'black' || color === 'white' ? color : ''
}

function getUserUuid(userUuid?: string | null): string {
  if (userUuid != null) return String(userUuid).trim()
  try { return String(localStorage.getItem('uuid') || '').trim() } catch { return '' }
}

function isFactoryAccount(userUuid?: string | null): boolean {
  return getUserUuid(userUuid) === FACTORY_ACCOUNT_UUID
}

function getDeviceColor(tab: FirmwareTab, detail?: Record<string, unknown> | null): 'black' | 'white' | '' {
  if (!detail) return ''
  const value = tab === 'dongle' ? (detail.colorName ?? detail.color) : (detail.headsetColorName ?? detail.headsetColor)
  return normalizeColor(value)
}

function parseFirmwareFileName(fileName: string): { color: 'black' | 'white' | ''; version: string } {
  const name = String(fileName || '').trim()
  const match = name.match(/(?:^|_)(v\d+(?:\.\d+)*(?:[-_]\d+)?)\.up$/i)
  let color: 'black' | 'white' | '' = ''
  for (const segment of name.replace(/\.up$/i, '').split(/[_.]/)) {
    const parsed = normalizeColor(segment)
    if (parsed) { color = parsed; break }
  }
  return { color, version: match ? `V${match[1].replace(/^[vV]/, '')}` : '' }
}

function buildFirmwareUrl(tab: FirmwareTab, version: string, fileName?: string): string {
  const folder = FIRMWARE_FOLDER_MAP[tab]
  if (!folder) return ''

  // 如果有 fileName，直接使用
  if (fileName) {
    const base = import.meta.env.BASE_URL || '/'
    return `${base}upgrading/${folder}/${fileName}`
  }

  // 否则从缓存数据中查找
  const items = cachedFirmwareData[tab] || []
  const item = items.find(entry => entry.version === version)
  if (item?.fileName) {
    const base = import.meta.env.BASE_URL || '/'
    return `${base}upgrading/${folder}/${item.fileName}`
  }

  return ''
}

function normalizeFirmwareItem(tab: FirmwareTab, item: any) {
  const fileName = String(item.fileName || '').trim()
  const parsed = parseFirmwareFileName(fileName)
  return {
    ...item,
    version: parsed.version || item.version,
    fileName,
    color: parsed.color || item.color,
    downloadUrl: item.downloadUrl || buildFirmwareUrl(tab, item.version, fileName)
  }
}

function getVisibleRawList(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  const factory = isFactoryAccount(userUuid)
  const items = cachedFirmwareData[tab] || []
  return items
    .filter(item => factory || !item.factoryOnly)
    .map(item => normalizeFirmwareItem(tab, item))
}

export function getFirmwareHistoryList(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  // 先尝试从后端获取最新数据（异步，不阻塞）
  fetchBackendFirmware().catch(() => {})

  let list = getVisibleRawList(tab, detail, userUuid)

  // 颜色过滤（仅对 earphone 和 dongle，工厂账号除外）
  if (!isFactoryAccount(userUuid) && (tab === 'earphone' || tab === 'dongle')) {
    const deviceColor = getDeviceColor(tab, detail)
    if (deviceColor) {
      list = list.filter(item => !item.color || item.color === deviceColor)
    } else {
      list = []
    }
  }

  // 版本号过滤：只显示与当前设备版本前两段一致的固件
  const currentVersion = getDeviceCurrentVersion(tab, detail)
  if (currentVersion && list.length > 0) {
    list = list.filter(item => isMajorMinorMatch(item.version, currentVersion))
  }

  return list
}

function getColorMatchedListForLatest(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  const list = getVisibleRawList(tab, detail, userUuid)
  if (tab !== 'earphone' && tab !== 'dongle') return list
  const deviceColor = getDeviceColor(tab, detail)
  if (deviceColor) return list.filter(item => !item.color || item.color === deviceColor)
  return isFactoryAccount(userUuid) ? list : []
}

export function getFirmwareMockData(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null): FirmwareMockItem {
  const historyList = getFirmwareHistoryList(tab, detail, userUuid)
  const latest = getColorMatchedListForLatest(tab, detail, userUuid)
    .slice()
    .sort((a, b) => compareFirmwareVersion(b.version, a.version))[0]

  return {
    deviceName: FIRMWARE_FOLDER_MAP[tab] || '',
    latestVersion: latest?.version || '',
    latestFileName: latest?.fileName || '',
    latestDownloadUrl: latest?.downloadUrl || '',
    size: latest?.fileSize ? `${Math.round(latest.fileSize / 1024)} KB` : '',
    description: latest?.description || '',
    historyList
  }
}

export function getDeviceCurrentVersion(tab: FirmwareTab, detail?: Record<string, unknown> | null): string {
  const version = detail?.[VERSION_KEY_MAP[tab]]
  return typeof version === 'string' && version.trim() ? version.trim() : ''
}

export function hasFirmwareUpdate(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null): boolean {
  const current = getDeviceCurrentVersion(tab, detail)
  const latest = getFirmwareMockData(tab, detail, userUuid).latestVersion
  return !!(current && latest && compareFirmwareVersion(latest, current) > 0)
}

export function getTabsWithFirmwareUpdate(detail?: Record<string, unknown> | null, userUuid?: string | null): FirmwareTab[] {
  return FIRMWARE_TABS.filter(tab => hasFirmwareUpdate(tab, detail, userUuid))
}

export function hasAnyFirmwareUpdate(detail?: Record<string, unknown> | null, userUuid?: string | null): boolean {
  return getTabsWithFirmwareUpdate(detail, userUuid).length > 0
}

export { buildFirmwareUrl, FACTORY_ACCOUNT_UUID }
